import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { store } from '@/common/root';
import type { FeatureFlag } from '@schema';
import CommonError from '@/common/utils/errors/CommonError';
import NotFoundError from '@/common/utils/errors/NotFoundError';
import useAppModeStore from '../appMode';

interface Feature {
  id: string;
  enabled: boolean;
  title: string;
  description: string;
  metadata?: {
    target: 'common' | 'self-service' | 'hci';
  };
}

const REMOTE_FEATURE_FLAGS_URL = '/api/v2/features';

export const useFeaturesStore = defineStore('features', () => {
  const features = ref<Feature[]>([]);

  const remoteFeatureFlags = ref<FeatureFlag[]>([]);

  const availableFeatures = computed(() => features.value.filter(
    feature => !feature.metadata?.target
      || feature.metadata.target === 'common'
      || feature.metadata.target === APP_BUILD
  ));

  const findFeature = computed(
    () => (featureId: string) => availableFeatures.value.find(feature => feature.id === featureId)
  );

  const isSwitchToCyberframeEnabled = computed(
    () => remoteFeatureFlags.value.find(flag => flag.name === 'switch-to-cyberframe')?.enabled ?? false
  );

  const fetchRemoteFeatureFlags = async () => {
    try {
      const response = await store.$api.get<FeatureFlag[]>(REMOTE_FEATURE_FLAGS_URL);
      remoteFeatureFlags.value = response.data || [];
    } catch (err) {
      // Backends older than 20.0.21 (the 6.3-7.2 lines) have no such route and answer 404
      // (VSTOR-146910). No flags means every flag off, which is what the getters already fall
      // back to. Reading it must also not reject: nothing catches it further up, so autocapture
      // would report it a second time.
      //
      // Only an absent endpoint clears the flags: HeaderAlerts remounts with the layout, and a
      // transient failure must not discard flags an earlier call had read. The transport owns
      // reporting for what it raises, so only a throw that never reached it is logged here.
      if (err instanceof NotFoundError) {
        remoteFeatureFlags.value = [];
      } else if (!(err instanceof CommonError)) {
        console.error('Error while reading remote feature flags: ', err);
      }
    }
  };

  const fetchItems = async () => {
    try {
      const appModeStore = useAppModeStore();
      const res: FetchResponse<{ features: Feature[] }> = await store.$api.get(appModeStore.preset.featuresUrl, undefined, appModeStore.preset.featuresConfig);
      features.value = res.data.features;
    } catch (err) {
      // Features config may absent, or contain an error.
      // Notify but do not block the application
      if (err instanceof CommonError) {
        console.error('Error while reading features.json file: ', err);
      }
      return err;
    }
    return Promise.resolve();
  };

  return {
    features,
    remoteFeatureFlags,
    availableFeatures,
    findFeature,
    isSwitchToCyberframeEnabled,
    fetchRemoteFeatureFlags,
    fetchItems
  };
});

export default useFeaturesStore;
